import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import OTPInput from "react-otp-input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye, faEyeSlash } from "@fortawesome/free-regular-svg-icons";
import callApi from "services/api";
import { useRecoilState } from "recoil";
import { AlertState } from "global/AlertState";
import { encryptByRSA } from "utils/CryptoUtils";

const Otp = ({ registrationNumber, onEdit, setLoginActiveView }) => {
  const [timer, setTimer] = useState(30);
  const [isResendEnabled, setIsResendEnabled] = useState(false);
  const [, setAlertMessage] = useRecoilState(AlertState);
  const [, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    let countdown;
    if (timer > 0) {
      countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setIsResendEnabled(true);
      clearInterval(countdown);
    }
    return () => clearInterval(countdown);
  }, [timer]);

  const handleResendOTP = () => {
    setTimer(30);
    setIsResendEnabled(false);
  };

  const formik = useFormik({
    initialValues: {
      registration_no: registrationNumber,
      otp: "",
      new_password: "",
      confirm_password: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required("OTP is required")
        .matches(/^\d{6}$/, "OTP must be exactly 6 digits"),
      new_password: Yup.string()
        .required("New Password is required")
        .min(8, "Password must be at least 8 characters")
        .matches(
          /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]/,
          "Password must contain at least one letter and one number"
        ),
      confirm_password: Yup.string()
        .required("Confirm Password is required")
        .oneOf([Yup.ref("new_password")], "Passwords must match"),
    }),
    onSubmit: async (values) => {
      const encryptedData = encryptByRSA(JSON.stringify(values));
      setLoading(true);
      const endpoint = `auth/reset-password`;
      const response = await callApi(
        endpoint,
        "POST",
        encryptedData,
        false,
        false,
        setAlertMessage,
        null,
        null
      );
      setLoading(false);
      if (response?.success) {
        setAlertMessage(
          "Password reset successfully. Please log in with your new password!|true"
        );
        setLoginActiveView("login");
      }
    },
  });

  return (
    <>
      <h5 className="mb-3 text-center" style={{ fontWeight: "bold" }}>
        Verify OTP & Set Password
      </h5>
      <form onSubmit={formik.handleSubmit}>
        <div className="mb-4 text-center d-flex justify-content-center">
          <OTPInput
            value={formik.values.otp}
            onChange={(otp) => formik.setFieldValue("otp", otp)}
            numInputs={6}
            renderInput={(props) => (
              <input
                {...props}
                className={`otp-input ${
                  formik.errors.otp && formik.touched.otp ? "is-invalid" : ""
                }`}
                style={{
                  width: "35px",
                  height: "40px",
                  fontSize: "16px",
                  margin: "0 3px",
                  textAlign: "center",
                  borderRadius: "4px",
                  border:
                    formik.errors.otp && formik.touched.otp
                      ? "1px solid red"
                      : "1px solid #ccc",
                }}
              />
            )}
          />
        </div>
        {formik.errors.otp && formik.touched.otp && (
          <div className="text-danger text-center mb-3">
            {formik.errors.otp}
          </div>
        )}

        <div className="mb-3 position-relative">
          <input
            type={showPassword ? "text" : "password"}
            name="new_password"
            className={`form-control ${
              formik.errors.new_password && formik.touched.new_password
                ? "is-invalid"
                : ""
            }`}
            placeholder="Enter New Password"
            value={formik.values.new_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <span
            className="position-absolute end-0 top-50 translate-middle-y me-3"
            style={{ cursor: "pointer" }}
            onClick={() => setShowPassword((prev) => !prev)}
          >
            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
          </span>
          {formik.errors.new_password && formik.touched.new_password && (
            <div className="text-danger">{formik.errors.new_password}</div>
          )}
        </div>

        <div className="mb-3 position-relative">
          <input
            type={showConfirmPassword ? "text" : "password"}
            name="confirm_password"
            className={`form-control ${
              formik.errors.confirm_password && formik.touched.confirm_password
                ? "is-invalid"
                : ""
            }`}
            placeholder="Re-enter Password"
            value={formik.values.confirm_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <span
            className="position-absolute end-0 top-50 translate-middle-y me-3"
            style={{ cursor: "pointer" }}
            onClick={() => setShowConfirmPassword((prev) => !prev)}
          >
            <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
          </span>
          {formik.errors.confirm_password &&
            formik.touched.confirm_password && (
              <div className="text-danger">
                {formik.errors.confirm_password}
              </div>
            )}
        </div>

        <div className="d-flex justify-content-center">
          <button
            type="submit"
            className="btn btn-sm btn-info"
            disabled={!formik.isValid || formik.isSubmitting}
          >
            Submit
          </button>
        </div>
      </form>

      <p className="mt-2 text-center">
        {timer > 0 ? (
          <span>Resend OTP in: {timer} secs</span>
        ) : (
          <button
            className="btn btn-link"
            onClick={handleResendOTP}
            disabled={!isResendEnabled}
          >
            Resend OTP
          </button>
        )}
        <br />
        <strong>
          Wrong Application Number?{" "}
          <span style={{ color: "blue" }}>{registrationNumber}</span>{" "}
          <FontAwesomeIcon icon={faEdit} onClick={onEdit} />
        </strong>
      </p>

      <p className="footer-text mb-5 text-center">
        By continuing, you agree to the Terms & Conditions and Privacy Policy.
      </p>
    </>
  );
};

export default Otp;
